/**
 * Schema markup utilities for SEO optimization
 * Provides structured data for different page types to improve search visibility
 */

// Base organization schema
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Tap'nGrab",
  "description": "AI-powered smart coolers for offices, gyms, residential buildings in Montreal & South Shore. Zero-cost, contactless, fully managed amenity.",
  "url": "https://tapngrab.ca",
  "logo": "https://tapngrab.ca/images/tapngrab_logo_s.png",
  "foundingDate": "2024",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Montreal",
    "addressRegion": "QC",
    "addressCountry": "CA"
  },
  "areaServed": [
    {
      "@type": "City",
      "name": "Montreal"
    },
    {
      "@type": "City", 
      "name": "South Shore"
    }
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "url": "https://tapngrab.ca/contact"
  },
  "sameAs": []
});

// Service schema for different landing pages
export const getServiceSchema = (pageType, language = 'en') => {
  const serviceData = {
    warehouses: {
      en: {
        name: "Smart Coolers for Warehouses",
        description: "On-site smart coolers for warehouse workers in Montreal & South Shore. Zero-cost amenity, 24/7 access, perfect for distribution centers.",
        category: "Industrial Vending Solutions",
        serviceType: "Smart Vending Machines",
        targetAudience: "Warehouse Workers, Distribution Centers, Logistics Companies"
      },
      fr: {
        name: "Distributeurs intelligents pour entrepôts",
        description: "Distributeurs intelligents sur site pour travailleurs d'entrepôt à Montréal et Rive-Sud. Aménité à zéro coût, accès 24/7, parfait pour centres de distribution.",
        category: "Solutions de distribution industrielle",
        serviceType: "Distributeurs intelligents",
        targetAudience: "Travailleurs d'entrepôt, Centres de distribution, Entreprises logistiques"
      }
    },
    offices: {
      en: {
        name: "Smart Coolers for Offices",
        description: "Enhance your office with zero-cost smart coolers. Perfect for corporate buildings with 50+ employees. AI-powered, contactless, fully managed.",
        category: "Corporate Amenities",
        serviceType: "Smart Vending Machines",
        targetAudience: "Office Workers, Corporate Buildings, Business Centers"
      },
      fr: {
        name: "Distributeurs intelligents pour bureaux",
        description: "Améliorez votre bureau avec des distributeurs intelligents à zéro coût. Parfait pour les immeubles de bureaux avec 50+ employés. Alimenté par IA, sans contact, entièrement géré.",
        category: "Aménités corporatives",
        serviceType: "Distributeurs intelligents",
        targetAudience: "Employés de bureau, Immeubles corporatifs, Centres d'affaires"
      }
    },
    residential: {
      en: {
        name: "Smart Coolers for Residential Buildings",
        description: "Add a premium amenity to your condo or apartment building at zero cost. Smart coolers for Montreal & South Shore residential properties.",
        category: "Residential Amenities",
        serviceType: "Smart Vending Machines",
        targetAudience: "Residents, Property Managers, Condo Boards"
      },
      fr: {
        name: "Distributeurs intelligents pour immeubles résidentiels",
        description: "Ajoutez une aménité premium à votre condo ou immeuble d'appartements à zéro coût. Distributeurs intelligents pour propriétés résidentielles de Montréal et Rive-Sud.",
        category: "Aménités résidentielles",
        serviceType: "Distributeurs intelligents",
        targetAudience: "Résidents, Gestionnaires immobiliers, Conseils de copropriété"
      }
    },
    hotels: {
      en: {
        name: "Smart Coolers for Hotels",
        description: "Premium smart coolers for hotel guests in Montreal. Modern alternative to vending machines with AI vision technology.",
        category: "Hospitality Solutions",
        serviceType: "Smart Vending Machines",
        targetAudience: "Hotel Guests, Hospitality Industry, Hotel Management"
      },
      fr: {
        name: "Distributeurs intelligents pour hôtels",
        description: "Distributeurs intelligents premium pour clients d'hôtel à Montréal. Alternative moderne aux distributeurs automatiques avec technologie de vision IA.",
        category: "Solutions d'hospitalité",
        serviceType: "Distributeurs intelligents",
        targetAudience: "Clients d'hôtel, Industrie hôtelière, Gestion hôtelière"
      }
    },
    campuses: {
      en: {
        name: "Smart Coolers for University Campuses",
        description: "AI-powered smart coolers for university and college campuses in Montreal. Perfect for student centers, libraries, and academic buildings.",
        category: "Educational Solutions",
        serviceType: "Smart Vending Machines",
        targetAudience: "Students, Faculty, University Staff, Campus Administrators"
      },
      fr: {
        name: "Distributeurs intelligents pour campus universitaires",
        description: "Distributeurs intelligents alimentés par IA pour campus universitaires et collégiaux à Montréal. Parfait pour centres étudiants, bibliothèques et bâtiments académiques.",
        category: "Solutions éducatives",
        serviceType: "Distributeurs intelligents",
        targetAudience: "Étudiants, Professeurs, Personnel universitaire, Administrateurs de campus"
      }
    },
    hospitals: {
      en: {
        name: "Smart Coolers for Hospitals & Clinics",
        description: "24/7 smart coolers for hospital staff and visitors in Montreal. Perfect for staff lounges, waiting areas, and medical facilities.",
        category: "Healthcare Solutions",
        serviceType: "Smart Vending Machines",
        targetAudience: "Healthcare Workers, Hospital Staff, Medical Visitors, Healthcare Administrators"
      },
      fr: {
        name: "Distributeurs intelligents pour hôpitaux et cliniques",
        description: "Distributeurs intelligents 24/7 pour personnel hospitalier et visiteurs à Montréal. Parfait pour salles de personnel, salles d'attente et installations médicales.",
        category: "Solutions de soins de santé",
        serviceType: "Distributeurs intelligents",
        targetAudience: "Travailleurs de la santé, Personnel hospitalier, Visiteurs médicaux, Administrateurs de soins de santé"
      }
    },
    gyms: {
      en: {
        name: "Smart Coolers for Gyms & Fitness Centers",
        description: "Smart coolers with healthy snacks and proteins for gym members in Montreal. Zero-cost amenity for fitness centers with 80+ daily visits.",
        category: "Fitness Solutions",
        serviceType: "Smart Vending Machines",
        targetAudience: "Gym Members, Fitness Enthusiasts, Gym Owners, Fitness Center Staff"
      },
      fr: {
        name: "Distributeurs intelligents pour gymnases et centres de fitness",
        description: "Distributeurs intelligents avec collations saines et protéines pour membres de gym à Montréal. Aménité à zéro coût pour centres de fitness avec 80+ visites quotidiennes.",
        category: "Solutions de fitness",
        serviceType: "Distributeurs intelligents",
        targetAudience: "Membres de gym, Passionnés de fitness, Propriétaires de gym, Personnel de centre de fitness"
      }
    }
  };

  const data = serviceData[pageType]?.[language] || serviceData[pageType]?.en;
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": data.name,
    "description": data.description,
    "provider": {
      "@type": "Organization",
      "name": "Tap'nGrab",
      "url": "https://tapngrab.ca"
    },
    "serviceType": data.serviceType,
    "category": data.category,
    "areaServed": [
      {
        "@type": "City",
        "name": "Montreal"
      },
      {
        "@type": "City",
        "name": "South Shore"
      }
    ],
    "audience": {
      "@type": "Audience",
      "audienceType": data.targetAudience
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "CAD",
      "description": "Zero-cost installation and management",
      "availability": "https://schema.org/InStock"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Smart Cooler Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Installation",
            "description": "Free professional installation"
          }
        },
        {
          "@type": "Offer", 
          "itemOffered": {
            "@type": "Service",
            "name": "Management",
            "description": "Fully managed restocking and maintenance"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service", 
            "name": "Analytics",
            "description": "Real-time sales and inventory analytics"
          }
        }
      ]
    }
  };
};

// Breadcrumb schema
export const getBreadcrumbSchema = (breadcrumbs, language = 'en') => {
  const baseUrl = 'https://tapngrab.ca';
  const langPrefix = language === 'fr' ? '/fr' : '';
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${baseUrl}${langPrefix}${crumb.url}`
    }))
  };
};

// FAQ schema
export const getFAQSchema = (faqs, language = 'en') => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Local business schema
export const getLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Tap'nGrab",
  "description": "AI-powered smart coolers for offices, gyms, residential buildings in Montreal & South Shore",
  "url": "https://tapngrab.ca",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Montreal",
    "addressRegion": "QC",
    "addressCountry": "CA"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "45.5017",
    "longitude": "-73.5673"
  },
  "openingHours": "Mo-Su 00:00-23:59", // 24/7 service
  "serviceArea": [
    {
      "@type": "City",
      "name": "Montreal"
    },
    {
      "@type": "City",
      "name": "South Shore"
    }
  ],
  "priceRange": "$$",
  "paymentAccepted": ["Credit Card", "Debit Card", "Mobile Payment"]
});

// Product schema for smart coolers
export const getProductSchema = (language = 'en') => {
  const productData = {
    en: {
      name: "Tap'nGrab Smart Cooler",
      description: "AI-powered smart cooler with contactless payment, real-time analytics, and automated restocking. Perfect for offices, gyms, and residential buildings.",
      brand: "Tap'nGrab",
      category: "Smart Vending Machines"
    },
    fr: {
      name: "Distributeur intelligent Tap'nGrab",
      description: "Distributeur intelligent alimenté par IA avec paiement sans contact, analytiques en temps réel et réapprovisionnement automatisé. Parfait pour bureaux, gymnases et immeubles résidentiels.",
      brand: "Tap'nGrab", 
      category: "Distributeurs intelligents"
    }
  };

  const data = productData[language] || productData.en;

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": data.name,
    "description": data.description,
    "brand": {
      "@type": "Brand",
      "name": data.brand
    },
    "category": data.category,
    "manufacturer": {
      "@type": "Organization",
      "name": "Tap'nGrab"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "CAD",
      "availability": "https://schema.org/InStock",
      "description": "Free installation and management"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "50"
    }
  };
};



